import * as Sentry from '@sentry/browser';
import * as React from 'react';
import { ErrorInfo, ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import Typography from "@material-ui/core/Typography";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";

interface Props {
    error: Error | null,
    children: ReactNode,
}

interface State {
    componentError: Error | null,
    errorInfo: ErrorInfo | null,
}

const styles = (theme: Theme) => createStyles({
    root: {
        maxWidth: 500,
        margin: '25vh auto 0',
    },
    title: {
        marginBottom: 2 * theme.spacing.unit,
    },
    buttonBar: {
        marginTop: 4 * theme.spacing.unit,
        justifyContent: 'space-between',
    }
});

type Styles = 'root' | 'title' | 'buttonBar';

class SentryErrorBoundary extends React.Component<Props & WithStyles<Styles>, State> {
    constructor(props: Props & WithStyles<Styles>) {
        super(props);
        this.state = {componentError: null, errorInfo: null};
    }

    public componentDidCatch(componentError: Error, errorInfo: ErrorInfo) {
        console.log("componentDidCatch", componentError, errorInfo);
        this.setState({componentError, errorInfo});
    }

    public render() {
        if (this.state.componentError || this.props.error) {
            const {classes} = this.props;
            return (
                <div className={classes.root}>
                    <Typography variant="h5" align="center" className={classes.title}>
                        <FormattedMessage id="error_page_title"/>
                    </Typography>
                    <Typography variant="body1">
                        <FormattedMessage id="error_page_text_1"/>
                    </Typography>
                    <Typography variant="body1">
                        <FormattedMessage id="error_page_text_2"/>
                    </Typography>
                    <div className={`${classes.buttonBar} hbox`}>
                        <Button onClick={this.handleSendErrorReport}>
                            <FormattedMessage id="error_page_send_report"/>
                        </Button>
                        <Button color="primary" onClick={this.handleReload}>
                            <FormattedMessage id="error_page_reload"/>
                        </Button>
                    </div>
                </div>
            );
        } else {
            return this.props.children;
        }
    }

    private handleSendErrorReport = () => {
        const error = this.props.error || this.state.componentError;
        if (error) {
            Sentry.withScope(scope => {
                const errorInfo = this.state.errorInfo;
                if (errorInfo) {
                    Object.keys(errorInfo).forEach(key => {
                        scope.setExtra(key, errorInfo[key]);
                    });
                }
                Sentry.captureException(error);
            });
        }
        Sentry.showReportDialog(
            {
                lang: 'de',
                title: 'Ein unerwarteter Fehler ist aufgetreten',
                successMessage: 'Ihr Feedback wurde versendet. Vielen Dank!'
            }
        );
    };

    private handleReload = () => {
        location.reload();
    };

}

export default withStyles(styles)(SentryErrorBoundary);



// WEBPACK FOOTER //
// ./src/components/SentryErrorBoundary.tsx